// Regras puras do Dashboard (/).
// Mockup: docs/design/aeros-dark/mockups/Tela Aeros Dark - Dashboard.dc.html
import { i18n } from "../../translate/i18n";

/**
 * Saudação do cabeçalho, pela hora local.
 * Faixas: 5h–11h bom dia, 12h–17h boa tarde, o resto boa noite.
 */
export const saudacao = (data = new Date()) => {
  const h = data.getHours();
  if (h >= 5 && h < 12) return i18n.t("aeros.dashboard.greeting.morning");
  if (h >= 12 && h < 18) return i18n.t("aeros.dashboard.greeting.afternoon");
  return i18n.t("aeros.dashboard.greeting.evening");
};

/**
 * "atualizado há 30s" / "há 5 min" / "há 2 h".
 * Abaixo de 10s trata como agora — piscar "há 1s" a cada segundo é ruído.
 */
export const atualizadoHa = (segundos) => {
  const s = Math.max(0, Math.floor(Number(segundos) || 0));
  if (s < 10) return i18n.t("aeros.dashboard.updatedNow");
  if (s < 60) return i18n.t("aeros.dashboard.updatedSeconds", { count: s });
  const min = Math.floor(s / 60);
  if (min < 60) return i18n.t("aeros.dashboard.updatedMinutes", { count: min });
  const horas = Math.floor(min / 60);
  return i18n.t("aeros.dashboard.updatedHours", { count: horas });
};

/**
 * Minutos -> "07:42" (hh:mm), como no mockup.
 *
 * O backend manda os tempos médios em MINUTOS (fracionários). Valor negativo
 * ou inválido vira "00:00": tempo negativo é dado sujo, não informação.
 */
export const formatDuracao = (minutos) => {
  const m = Number(minutos);
  if (!Number.isFinite(m) || m <= 0) return "00:00";
  const total = Math.round(m);
  const h = Math.floor(total / 60);
  const resto = total % 60;
  return `${String(h).padStart(2, "0")}:${String(resto).padStart(2, "0")}`;
};

/**
 * NPS = % promotores − % detratores, arredondado. Vai de −100 a 100.
 * Sem nenhuma avaliação o score é 0, e não NaN.
 */
export const npsScore = (promotores, detratores) => {
  const p = Number(promotores) || 0;
  const d = Number(detratores) || 0;
  return Math.round(p - d);
};

/**
 * Cor do score no mockup: verde a partir de 50, âmbar de 0 a 49, vermelho
 * abaixo de zero (mais detratores que promotores).
 */
export const npsTone = (score) => {
  const n = Number(score) || 0;
  if (n >= 50) return "success";
  if (n >= 0) return "warning";
  return "error";
};

// Fatias do donut, em porcentagem já normalizada para somar 100.
export const npsSlices = (counters = {}) => {
  const p = Number(counters.npsPromotersPerc) || 0;
  const n = Number(counters.npsPassivePerc) || 0;
  const d = Number(counters.npsDetractorsPerc) || 0;
  const soma = p + n + d;
  if (soma <= 0) return { promoters: 0, passive: 0, detractors: 0, vazio: true };
  return {
    promoters: (p / soma) * 100,
    passive: (n / soma) * 100,
    detractors: (d / soma) * 100,
    vazio: false,
  };
};

/**
 * Status do atendente para a pill do mockup.
 * "Ocupado" é quem está online com atendimento aberto — o mockup separa isso
 * de quem está online e livre.
 */
export const agentStatus = (attendant = {}) => {
  if (!attendant.online) return "offline";
  if (Number(attendant.openTickets) > 0) return "busy";
  return "online";
};

export const agentStatusLabel = (status) =>
  i18n.t(`aeros.dashboard.agentStatus.${status}`);

/**
 * Nota do atendente: cor por faixa, como no mockup (>=4,5 verde; >=4 âmbar;
 * abaixo disso vermelho). Sem avaliação não tem cor de alerta — zero aqui
 * significa "ninguém avaliou", não "nota péssima".
 */
export const ratingTone = (rating, countRating) => {
  if (!Number(countRating)) return "neutral";
  const r = Number(rating) || 0;
  if (r >= 4.5) return "success";
  if (r >= 4) return "warning";
  return "error";
};

export const ratingPercent = (rating) => {
  const r = Number(rating) || 0;
  if (r <= 0) return 0;
  return Math.min(100, (r / 5) * 100);
};

/**
 * Rótulo da fila do atendente: "Comercial" ou "Comercial +2" quando ele
 * atende mais de uma. Sem fila nenhuma, um traço.
 */
export const queueLabel = (queueName, queueCount) => {
  if (!queueName) return "—";
  const extras = Math.max(0, (Number(queueCount) || 1) - 1);
  return extras > 0 ? `${queueName} +${extras}` : queueName;
};

// "6 online de 14" no cabeçalho do card de atendentes.
export const onlineSummary = (attendants = []) => {
  const online = attendants.filter((a) => a.online).length;
  return i18n.t("aeros.dashboard.onlineOf", {
    online,
    total: attendants.length,
  });
};

/**
 * Barras do gráfico. Normaliza pela maior barra do período (recebidos ou
 * resolvidos) para as alturas serem comparáveis entre si; sem isso um dia com
 * 2 resolvidos e 0 recebidos desenharia uma barra cheia.
 */
export const chartBars = (data = []) => {
  const linhas = data.map((d) => ({
    label: d.data || (d.horario !== undefined ? `${d.horario}h` : ""),
    recebidos: Number(d.recebidos ?? d.total) || 0,
    resolvidos: Number(d.resolvidos) || 0,
  }));
  const maior = linhas.reduce(
    (max, l) => Math.max(max, l.recebidos, l.resolvidos),
    0
  );
  return linhas.map((l) => ({
    ...l,
    pctRecebidos: maior > 0 ? (l.recebidos / maior) * 100 : 0,
    pctResolvidos: maior > 0 ? (l.resolvidos / maior) * 100 : 0,
  }));
};

export const chartTotal = (data = []) =>
  data.reduce((soma, d) => soma + (Number(d.recebidos ?? d.total) || 0), 0);

/**
 * Quebra o texto do delta ("▲ 12% no período") no que o mockup mostra no
 * canto do card: seta + número. O texto inteiro vira title do elemento, para
 * não perder o "vs. média" / "na última hora".
 */
export const deltaCompacto = (delta) => {
  if (!delta || !delta.text) return null;
  const texto = String(delta.text);
  const subindo = texto.startsWith("▲");
  const desceu = texto.startsWith("▼");
  if (!subindo && !desceu) {
    return { valor: texto, direcao: null, tone: delta.tone, titulo: texto };
  }
  const semSeta = texto.replace(/^[▲▼]\s*/, "");
  const valor = semSeta.split(" ")[0];
  return {
    valor,
    direcao: subindo ? "up" : "down",
    tone: delta.tone,
    titulo: texto,
  };
};
